import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, ShieldCheck, Globe } from 'lucide-react';

// EU member countries currently offered in Checkout (plus a few extras for safety).
const EU_COUNTRIES = new Set([
  'Slovakia', 'Czech Republic', 'Germany', 'Austria', 'Poland', 'Hungary',
  'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Portugal',
  'Ireland', 'Finland', 'Sweden', 'Denmark', 'Greece', 'Romania',
  'Bulgaria', 'Croatia', 'Slovenia', 'Estonia', 'Latvia', 'Lithuania',
  'Luxembourg', 'Malta', 'Cyprus',
]);

// Country specific seller VAT rates we publish on invoices today.
const VAT_BY_COUNTRY = {
  Slovakia: 23,
  'Czech Republic': 21,
};

// Strict-enough EU VAT ID format: 2-letter country prefix + 2..12 alphanumerics.
export const isLikelyEuVatId = (raw) => {
  if (!raw) return false;
  const v = raw.replace(/\s+/g, '').toUpperCase();
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(v);
};

const getNoticeMode = (country, vatId) => {
  if (!country) return 'sk';
  if (country === 'Slovakia') return 'sk';
  if (country === 'Czech Republic') return 'cz';
  if (EU_COUNTRIES.has(country)) {
    return isLikelyEuVatId(vatId) ? 'reverse' : 'eu_b2c';
  }
  return 'export';
};

const VatNotice = ({ country, vatId }) => {
  const { t } = useTranslation();
  const mode = getNoticeMode(country, vatId);

  let Icon = Info;
  let title = '';
  let text = '';
  let tone = 'bg-blue-50 border-blue-200 text-blue-900';

  if (mode === 'sk') {
    Icon = ShieldCheck;
    title = t('vat.sk_title', 'Cena obsahuje DPH');
    text = t('vat.sk_text', `Cena uvedená v košíku obsahuje slovenskú DPH ${VAT_BY_COUNTRY.Slovakia}%.`);
    tone = 'bg-emerald-50 border-emerald-200 text-emerald-900';
  } else if (mode === 'cz') {
    Icon = ShieldCheck;
    title = t('vat.cz_title', 'Cena obsahuje DPH');
    text = t('vat.cz_text', `Pro doručenie do ČR fakturujeme s českou DPH ${VAT_BY_COUNTRY['Czech Republic']}%.`);
    tone = 'bg-emerald-50 border-emerald-200 text-emerald-900';
  } else if (mode === 'eu_b2c') {
    Icon = Info;
    title = t('vat.eu_b2c_title', 'EÚ — DPH dodávateľa');
    text = t('vat.eu_b2c_text', `Cena obsahuje DPH dodávateľa (SK ${VAT_BY_COUNTRY.Slovakia}%). Pre B2B s platným IČ DPH zadajte VAT ID nižšie a uplatníme reverse charge.`);
    tone = 'bg-blue-50 border-blue-200 text-blue-900';
  } else if (mode === 'reverse') {
    Icon = ShieldCheck;
    title = t('vat.reverse_title', 'Reverse charge — bez DPH');
    text = t('vat.reverse_text', 'Pre B2B objednávky v rámci EÚ s platným VAT ID účtujeme bez DPH (reverse charge). VAT ID overíme manuálne.');
    tone = 'bg-purple-50 border-purple-200 text-purple-900';
  } else {
    Icon = Globe;
    title = t('vat.export_title', 'Export mimo EÚ — 0% DPH');
    text = t('vat.export_text', 'Faktúra bude vystavená bez DPH (0%). Dovozné clo a dane v cieľovej krajine hradí kupujúci.');
    tone = 'bg-amber-50 border-amber-200 text-amber-900';
  }

  return (
    <div
      className={`border rounded-lg p-3 flex items-start space-x-3 ${tone}`}
      data-testid="vat-notice"
      data-vat-mode={mode}
    >
      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <div className="font-semibold mb-0.5" data-testid="vat-notice-title">{title}</div>
        <div className="text-xs leading-relaxed" data-testid="vat-notice-text">{text}</div>
      </div>
    </div>
  );
};

export default VatNotice;
